import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { normalizePatientStatusForForm, patientStatusLabel } from '../../../lib/patientStatus';
import {
  PATIENT_GENDER_OPTIONS,
  buildPatientInitials,
  buildPatientNameFromParts,
  normalizePatientGender,
  validatePatientIdentityFields,
} from '../../../lib/patientIdentity';

// Icon component stub (replace with your actual Icon import)
const Icon = ({ name, size = 20, className = '' }) => {
  const icons = {
    X: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    ),
    Save: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>
      </svg>
    ),
    User: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
      </svg>
    ),
    AlertCircle: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    ),
    Check: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polyline points="20 6 9 13 4 10"/>
      </svg>
    ),
    Loader: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`${className} animate-spin`}>
        <line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/>
      </svg>
    ),
  };
  return icons[name] || null;
};

// ─── Field Config ──────────────────────────────────────────────────────────────
const FIELDS = [
  {
    section: 'Identité',
    fields: [
      { key: 'first_name',        label: 'Prénom',              type: 'text',   required: true,  col: 1 },
      { key: 'last_name',         label: 'Nom',                 type: 'text',   required: true,  col: 1 },
      { key: 'date_birth',        label: 'Date de Naissance',   type: 'date',   required: true,  col: 1 },
      { key: 'gender',            label: 'Sexe',                type: 'select', required: true,  col: 1,
        options: [{ value: '', label: 'Sélectionner' }, ...PATIENT_GENDER_OPTIONS],
      },
      { key: 'initials',          label: 'Initiales',           type: 'text',   required: false, col: 1, maxLength: 5 },
    ],
  },
  {
    section: 'Contact',
    fields: [
      { key: 'email',             label: 'Email',               type: 'email',  required: false, col: 2 },
      { key: 'phone',             label: 'Téléphone',           type: 'tel',    required: false, col: 1 },
      { key: 'location',          label: 'Localisation',        type: 'text',   required: false, col: 1 },
    ],
  },
  {
    section: 'Dossier',
    fields: [
      { key: 'patient_code',      label: 'Code Patient',        type: 'text',   required: true,  col: 1, readonly: true },
      { key: 'status',            label: 'Statut',              type: 'select', required: true,  col: 1,
        options: [
          { value: 'active',   label: 'Actif' },
          { value: 'inactive', label: 'Inactif' },
        ],
      },
      { key: 'active_cases',      label: 'Cas Actifs',          type: 'number', required: false, col: 1, min: 0 },
    ],
  },
];

const STATUS_STYLES = {
  active:   'bg-emerald-500/15 text-emerald-600 ring-emerald-500/30',
  inactive: 'bg-slate-400/15  text-slate-500   ring-slate-400/30',
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const toFormValues = (patient) => ({
  first_name:    patient?.first_name    ?? '',
  last_name:     patient?.last_name     ?? '',
  gender:        normalizePatientGender(patient?.gender) ?? '',
  initials:      patient?.initials      ?? '',
  email:         patient?.email         ?? '',
  phone:         patient?.phone         ?? '',
  location:      patient?.location      ?? '',
  date_birth:    patient?.date_birth    ?? '',
  patient_code:  patient?.patient_code  ?? '',
  status:        normalizePatientStatusForForm(patient?.status ?? 'active'),
  active_cases:  patient?.active_cases  !== undefined ? String(patient.active_cases) : '0',
});

const validate = (values) => {
  const errors = validatePatientIdentityFields(values);
  if (values.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = "Format d'email invalide.";
  }
  return errors;
};

// ─── Sub-components ────────────────────────────────────────────────────────────
const Field = ({ field, value, onChange, error }) => {
  const base =
    'w-full rounded-lg border bg-background px-3 py-2 text-sm text-foreground ' +
    'transition-all duration-150 outline-none ' +
    'focus:ring-2 focus:ring-primary/40 focus:border-primary ' +
    (error ? 'border-destructive ring-1 ring-destructive/40' : 'border-border hover:border-primary/50');

  if (field.type === 'select') {
    return (
      <select value={value} onChange={(e) => onChange(e.target.value)} className={base} disabled={field.readonly}>
        {field.options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    );
  }

  return (
    <input
      type={field.type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      readOnly={field.readonly}
      maxLength={field.maxLength}
      min={field.min}
      className={`${base} ${field.readonly ? 'opacity-60 cursor-not-allowed select-all' : ''}`}
    />
  );
};

// ─── Main Modal ────────────────────────────────────────────────────────────────
const PatientEditModal = ({ patient, isOpen, onClose, onSaved }) => {
  const [values,  setValues]  = useState({});
  const [errors,  setErrors]  = useState({});
  const [saving,  setSaving]  = useState(false);
  const [saved,   setSaved]   = useState(false);
  const [apiError, setApiError] = useState('');

  // Sync form when patient changes
  useEffect(() => {
    if (patient) {
      setValues(toFormValues(patient));
      setErrors({});
      setSaved(false);
      setApiError('');
    }
  }, [patient]);

  if (!isOpen || !patient) return null;

  const set = (key) => (val) => {
    setValues((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => { const e = { ...prev }; delete e[key]; return e; });
  };

  const handleSave = async () => {
    const errs = validate(values);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setSaving(true);
    setApiError('');

    const firstName = values.first_name.trim();
    const lastName = values.last_name.trim();
    const payload = {
      first_name:   firstName,
      last_name:    lastName,
      gender:       normalizePatientGender(values.gender),
      name:         buildPatientNameFromParts(firstName, lastName),
      initials:
        values.initials.trim() || buildPatientInitials(firstName, lastName) || null,
      email:        values.email.trim() || null,
      phone:        values.phone.trim()    || null,
      location:     values.location.trim() || null,
      date_birth:   values.date_birth,
      status:       normalizePatientStatusForForm(values.status),
      active_cases: parseInt(values.active_cases, 10) || 0,
      updated_at:   new Date().toISOString(),
    };

    try {
      const { data: updatedRows, error } = await supabase
        .from('patients')
        .update(payload)
        .eq('id', patient.id)
        .select('id')
        .maybeSingle();

      if (error) throw error;
      if (!updatedRows?.id) {
        throw new Error(
          'La modification n’a pas été enregistrée en base (aucune ligne mise à jour). Vérifiez vos droits d’écriture sur ce patient.'
        );
      }

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        onSaved?.({ ...patient, ...payload });
        onClose();
      }, 1000);
    } catch (err) {
      setApiError(err?.message || 'Une erreur est survenue lors de la sauvegarde.');
    } finally {
      setSaving(false);
    }
  };

  // Prevent closing while saving
  const handleClose = () => { if (!saving) onClose(); };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={handleClose}
      />

      {/* Panel */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="relative w-full max-w-2xl max-h-[90vh] flex flex-col
                     bg-card rounded-2xl border border-border shadow-2xl
                     animate-in slide-in-from-bottom-4 fade-in duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-border flex-shrink-0">
            <div className="flex items-center gap-3">
              {/* Avatar chip */}
              <div className="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-semibold text-primary">
                  {values.initials || values.name?.slice(0, 2).toUpperCase() || '??'}
                </span>
              </div>
              <div>
                <h2 className="text-base font-semibold text-foreground leading-tight">
                  Modifier le Patient
                </h2>
                <p className="text-xs text-muted-foreground">{values.patient_code}</p>
              </div>
            </div>

            {/* Status badge */}
            <div className="flex items-center gap-3">
              <span className={`hidden sm:inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ring-1 ${STATUS_STYLES[values.status] ?? STATUS_STYLES.active}`}>
                {patientStatusLabel(values.status)}
              </span>
              <button
                onClick={handleClose}
                disabled={saving}
                className="p-1.5 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors duration-150 disabled:opacity-40"
              >
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>

          {/* ── Body (scrollable) ── */}
          <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
            {FIELDS.map(({ section, fields }) => (
              <div key={section}>
                <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                  {section}
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-4">
                  {fields.map((field) => (
                    <div key={field.key} className={field.col === 2 ? 'col-span-2' : 'col-span-2 sm:col-span-1'}>
                      <label className="block text-xs text-muted-foreground mb-1.5">
                        {field.label}
                        {field.required && <span className="text-destructive ml-0.5">*</span>}
                        {field.readonly && <span className="ml-1 text-[10px] opacity-60">(lecture seule)</span>}
                      </label>
                      <Field
                        field={field}
                        value={values[field.key] ?? ''}
                        onChange={set(field.key)}
                        error={errors[field.key]}
                      />
                      {errors[field.key] && (
                        <p className="mt-1 text-xs text-destructive flex items-center gap-1">
                          <Icon name="AlertCircle" size={12} />
                          {errors[field.key]}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {/* API error */}
            {apiError && (
              <div className="flex items-start gap-2 rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                <Icon name="AlertCircle" size={16} className="flex-shrink-0 mt-0.5" />
                <span>{apiError}</span>
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-border flex-shrink-0 bg-muted/30 rounded-b-2xl">
            <p className="text-xs text-muted-foreground">
              {Object.keys(errors).length > 0
                ? <span className="text-destructive">Veuillez corriger les erreurs ci-dessus.</span>
                : 'Les champs marqués * sont obligatoires.'}
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                disabled={saving}
                className="px-4 py-2 rounded-lg text-sm font-medium text-muted-foreground
                           hover:bg-muted hover:text-foreground transition-colors duration-150
                           disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || saved}
                className={`relative px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2
                            transition-all duration-200 disabled:cursor-not-allowed
                            ${saved
                              ? 'bg-success text-white'
                              : 'bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-70'
                            }`}
              >
                {saving ? (
                  <>
                    <Icon name="Loader" size={15} />
                    Enregistrement…
                  </>
                ) : saved ? (
                  <>
                    <Icon name="Check" size={15} />
                    Enregistré !
                  </>
                ) : (
                  <>
                    <Icon name="Save" size={15} />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PatientEditModal;


